import React, { useEffect, useId, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { ChevronRight } from 'lucide-react';

/**
 * Disclosure — a headline that is always visible, and what lies behind it on
 * request (#1194). Testers on iPads read the AI analyses as "text on top of
 * text"; a view now shows each part's headline, and the part opens on a tap.
 *
 * The headline is something the data already has — a title, a count, a
 * number, a tag — never AI prose cut mid-sentence. Colours are inherited, so
 * it sits in the systemic-v2 (`BASE`) and future-reimagined (`THEME`) panels alike.
 *
 * `variant`:
 *  - `section` (default): a section heading, small caps, that opens its section
 *  - `inline`: a quiet link-sized line ("Lähteet (4)") inside a card
 *
 * Uncontrolled with `defaultOpen`, or controlled with `open` + `onOpenChange`
 * (a card that also opens when something else selects it).
 *
 * The body slides open and shut (grid rows 0fr ↔ 1fr, FOLD_MS); it is mounted
 * only while open or closing, and a reader who asks for less motion gets none.
 */

const Head = styled.button`
  display: flex; align-items: center; gap: 0.45rem; width: 100%;
  min-height: 44px; padding: 0.3rem 0; margin: 0;
  background: none; border: none; color: inherit; font: inherit; text-align: left; cursor: pointer;
  svg { flex-shrink: 0; transition: transform 0.15s ease; transform: rotate(${(p) => (p.$open ? 90 : 0)}deg); opacity: 0.75; }
  &:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; border-radius: 6px; }
`;

const Label = styled.span`
  ${(p) => (p.$variant === 'inline'
    ? 'font-size: 0.86rem; color: #9ecbff;'
    : 'font-size: 0.8rem; letter-spacing: 0.07em; text-transform: uppercase; opacity: 0.72; font-weight: 600;')}
`;

const Meta = styled.span`font-size: 0.82rem; opacity: 0.6; margin-left: auto; padding-left: 0.6rem; white-space: nowrap;`;

const Body = styled.div`padding: 0.2rem 0 0.6rem;`;

const FOLD_MS = 240;
/* Clipped only while moving, so a settled body's focus rings and shadows show. */
const Fold = styled.div`
  display: grid;
  grid-template-rows: ${(p) => (p.$expanded ? '1fr' : '0fr')};
  transition: grid-template-rows ${FOLD_MS}ms ease;
  > div { min-height: 0; overflow: ${(p) => (p.$settled ? 'visible' : 'hidden')}; }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

/**
 * closed → enter (mounted, shut) → opening (sliding open) → open;
 * open → closing (sliding shut) → closed. The first render takes the end state.
 */
function useFoldPhase(isOpen) {
  const [phase, setPhase] = useState(isOpen ? 'open' : 'closed');
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return undefined; }
    let frame = null;
    let timer = null;
    if (isOpen) {
      setPhase('enter');
      // Two frames, so the shut state is painted before the slide starts.
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => {
          setPhase('opening');
          timer = setTimeout(() => setPhase('open'), FOLD_MS);
        });
      });
    } else {
      setPhase('closing');
      timer = setTimeout(() => setPhase('closed'), FOLD_MS);
    }
    return () => { cancelAnimationFrame(frame); clearTimeout(timer); };
  }, [isOpen]);
  return phase;
}

export default function Disclosure({
  summary, meta = null, variant = 'section', defaultOpen = false, open, onOpenChange, children, testId,
}) {
  const [own, setOwn] = useState(defaultOpen);
  const controlled = open !== undefined;
  const isOpen = controlled ? open : own;
  const bodyId = useId();
  const phase = useFoldPhase(isOpen);
  const toggle = () => {
    if (!controlled) setOwn((o) => !o);
    onOpenChange?.(!isOpen);
  };
  return (
    <div data-testid={testId}>
      <Head type="button" $open={isOpen} aria-expanded={isOpen} aria-controls={bodyId} onClick={toggle}>
        <ChevronRight size={variant === 'inline' ? 15 : 17} aria-hidden />
        <Label $variant={variant}>{summary}</Label>
        {meta !== null && meta !== undefined ? <Meta>{meta}</Meta> : null}
      </Head>
      {phase !== 'closed' ? (
        <Fold $expanded={phase === 'opening' || phase === 'open'} $settled={phase === 'open'}>
          <div><Body id={bodyId}>{children}</Body></div>
        </Fold>
      ) : null}
    </div>
  );
}
